import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { endSession } from '../auth/session';
import { BootScreen } from '../components/BootScreen';
import { Sidebar } from '../components/Sidebar';
import { TaskDetail } from '../components/TaskDetail';
import { IconMenu } from '../components/icons';
import { useMe, useWorkspaces } from '../hooks/queries';
import { WorkspaceProvider } from '../workspace/WorkspaceContext';
import '../styles/layout.scss';
import '../styles/tasks.scss';
import '../styles/detail.scss';

export default function AppLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const workspaces = useWorkspaces(me.isSuccess);
  const [navOpen, setNavOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const taskId = searchParams.get('task');

  const closeTask = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
  }, [setSearchParams]);

  // A 403 on /users/me means this Google account has no access (not invited).
  // Sign out and hard-redirect to the login screen so the user can pick another
  // account, instead of spinning forever on a token that will always be rejected.
  const accessDenied = me.isError && me.error instanceof ApiError && me.error.status === 403;
  useEffect(() => {
    if (accessDenied) {
      void endSession().finally(() => window.location.assign('/login?denied=1'));
    }
  }, [accessDenied]);
  if (accessDenied) {
    return <BootScreen label={t('login.noAccess')} />;
  }

  if (me.isPending || workspaces.isPending) {
    return <BootScreen label={t('app.loading')} />;
  }

  if (me.isError || workspaces.isError || !workspaces.data?.length) {
    return (
      <div className="boot-screen">
        <span>{t('app.error')}</span>
        <button
          type="button"
          className="btn"
          onClick={() => {
            me.refetch();
            workspaces.refetch();
          }}
        >
          {t('app.retry')}
        </button>
      </div>
    );
  }

  const workspace = workspaces.data[0];

  const shellClass = ['shell'];
  if (taskId) shellClass.push('with-detail');
  if (navOpen) shellClass.push('nav-open');

  return (
    <WorkspaceProvider value={{ workspace, members: workspace.members }}>
      <div className={shellClass.join(' ')}>
        <Sidebar onNavigate={() => setNavOpen(false)} />
        <div
          className="nav-backdrop"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
        <div className="shell-main">
          <div className="mobile-topbar">
            <button
              type="button"
              className="icon-btn"
              onClick={() => setNavOpen(true)}
              aria-label={t('nav.menu')}
            >
              <IconMenu />
            </button>
            <span className="brand-name">{t('app.name')}</span>
            <span className="brand-mark" />
          </div>
          <Outlet />
        </div>
        {taskId && <TaskDetail taskId={taskId} onClose={closeTask} />}
      </div>
    </WorkspaceProvider>
  );
}
